import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Microsoft Logo SVG component
 * Official Microsoft brand colors
 */
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

/**
 * Loading Spinner component
 */
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * Login Page Component
 * Displays a centered login form with Microsoft SSO button
 */
export const Login = () => {
  const { isAuthenticated, isLoading, loginWithMicrosoft, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();

  // Check for error from OAuth callback
  const authError = searchParams.get('error');
  const displayError = authError === 'auth_failed'
    ? 'Authentication failed. Please try again.'
    : error;

  // If already authenticated, redirect to projects
  if (isAuthenticated) {
    return <Navigate to="/projects" replace />;
  }

  // Show loading while checking auth status
  if (isLoading) {
    return <LoadingSpinner />;
  }

  const handleLogin = () => {
    clearError();
    loginWithMicrosoft();
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div
        className="h-16 flex items-center justify-center shadow-md"
        style={{ background: 'linear-gradient(90deg, #4169E1 0%, #00BFFF 100%)' }}
      >
        <div className="flex items-center gap-1">
          <img src="/tilli-logo.png" alt="Tilli Software" className="h-8" />
          <span
            className="text-2xl text-slate-900 lowercase tracking-tight pb-1.5"
            style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
          >
            quality platform
          </span>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-grow flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold mb-2 text-center text-gray-900">
            Welcome
          </h1>
          <p className="text-gray-600 text-center mb-8">
            Sign in to access the Tilli Quality Platform
          </p>

          {/* Error Message */}
          {displayError && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{displayError}</span>
            </div>
          )}

          {/* Microsoft Sign In Button */}
          <button
            onClick={handleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 px-4 py-3 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <MicrosoftLogo />
            <span className="font-medium">Sign in with Microsoft</span>
          </button>

          <p className="mt-6 text-xs text-gray-500 text-center">
            By signing in, you agree to access this application using your organization credentials.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center text-xs text-gray-400">
        Tilli Quality Platform
      </div>
    </div>
  );
};

export default Login;
